import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HKBadge } from "@/components/site/HKBadge";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CTASection } from "@/components/site/CTASection";
import { DashboardShowcase } from "@/components/site/DashboardShowcase";
import { SupportedChannelsSection, PaymentMethodsSection } from "@/components/site/SupportedChannels";
import { DASHBOARD_FEATURES, BRAND } from "@/lib/content";
import { Seo } from "@/components/site/Seo";

export default function DashboardPage() {
  const { t } = useTranslation();
  const faqs = t("content.dashboardFaqs", { returnObjects: true }) || [];
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const breadcrumbs = [
    { name: t("seo.breadcrumbHome"), url: "/" },
    { name: t("seo.dashboard.breadcrumbLabel"), url: "/dashboard" },
  ];
  return (
    <div data-testid="dashboard-page">
      <Seo
        title={t("seo.dashboard.metaTitle")}
        description={t("seo.dashboard.metaDescription")}
        keywords={t("seo.dashboard.keywords")}
        path="/dashboard"
        jsonLd={faqJsonLd}
        breadcrumbs={breadcrumbs}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-14 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[58px] lg:leading-[1.05]">
            {t("pages.dashboardTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">
            {t("pages.dashboardSubtitle")}
          </p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.22em] text-blue-700">{BRAND.marketingLine}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="dashboard-hero-cta">
              <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                {t("cta.primary")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing"><Button variant="outline" className="h-11 rounded-lg border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50">{t("cta.seePricing")}</Button></Link>
          </div>
        </div>
      </section>

      <DashboardShowcase />

      <section id="features" data-testid="dashboard-features-section" className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <SectionHeading eyebrow={t("sections.capabilities.eyebrow")} title={t("sections.capabilities.title")} description={t("sections.capabilities.desc")} testIdPrefix="dashboard-features" />
        {DASHBOARD_FEATURES.map((m, idx) => {
          const reverse = idx % 2 === 1;
          const code = t(`content.${m.tk}.code`);
          const title = t(`content.${m.tk}.title`);
          const description = t(`content.${m.tk}.description`);
          const bullets = t(`content.${m.tk}.bullets`, { returnObjects: true }) || [];
          const governedBullets = t("common.governedBullets", { returnObjects: true }) || [];
          return (
            <motion.div
              key={m.key}
              id={m.key}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}
              className={`grid scroll-mt-24 gap-10 border-t border-slate-200 py-12 lg:grid-cols-2 lg:items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
              data-testid={`module-detail-${m.key}`}
            >
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">{code}</div>
                <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{title}</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{description}</p>
                <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {bullets.map((b) => (<li key={b} className="flex items-center gap-2 text-sm text-slate-800"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {b}</li>))}
                </ul>
                <Link to="/contact" data-testid={`module-cta-${m.key}`} className="mt-6 inline-block">
                  <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                    {t("cta.discussModule", { module: code })} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div>
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8">
                  <span className="absolute inset-x-0 top-0 h-1 bg-blue-600" />
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white text-blue-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)]"><m.icon className="h-8 w-8" /></div>
                  <h3 className="mt-6 text-xl font-extrabold tracking-tight text-slate-900">{t("common.governedCapability", { module: code })}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {t("common.governedBody")}
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-slate-800">
                    {governedBullets.map((g) => (
                      <li key={g} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {g}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      <SupportedChannelsSection />
      <PaymentMethodsSection />

      <section data-testid="faq-section">
        <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <SectionHeading eyebrow={t("sections.faq.eyebrow")} title={t("sections.faq.title")} align="center" testIdPrefix="faq" />
          <Accordion type="single" collapsible className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {faqs.map((f) => (
              <AccordionItem key={f.q} value={f.q} className="border-0 px-5">
                <AccordionTrigger data-testid={`faq-trigger-${f.q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`} className="text-left text-sm font-semibold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTASection title={t("ctaSection.dashboardTitle")} />
    </div>
  );
}
